/* eslint-disable no-plusplus */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _intersection from 'lodash/intersection';
import _difference from 'lodash/difference';
import { IconButton, Icon } from '@picsio/ui';
import {
  CheckIcon,
  CloseIcon,
  SearchPlusIcon,
  SearchIcon,
  Close,
  AddMoreFilters,
  ArrowTopSmall,
  ArrowBottomSmall,
  AutoSuggestDown,
} from '@picsio/icons';
import { usePrevious } from 'react-use';
import { MIME_TYPES } from '@picsio/db/src/constants';
import { useNavigate } from 'react-router-dom';

import SearchFilterItem from './SearchFilterItem';
import Logger from '../../services/Logger';
import picsioConfig from '../../../../../config';
import {
  BOOL_OPTIONS, PROOFING_FILTERS, SINGLE_OPERATORS, FILTER_NAMES_ASSETS, FILTER_NAMES_PRODUCTS, OPERATORS_ORDER, MULTIPLE_OPERATORS,
} from './config';
import AddFiltersBtn from './AddFiltersBtn';
import CatalogViewMode from '../CatalogViewMode';
import ToolbarSort from '../Sort';
import showAddNewFiltersDialog from '../../helpers/showSearchFiltersDialog';
import showSaveSearchDialog from '../SaveSearchDialog';
import l10n from '../../shared/strings';
import Tooltip from '../Tooltip';
import Autocomplete from '../Search/Autocomplete';
import * as utils from '../../shared/utils';
import {
  getSearchProps, isRouteSearch, reloadRoute, setSearchRoute,
  isRouteProducts,
  isRouteSelectedAssets,
} from '../../helpers/history';
import ua from '../../ua';
import { changeTree, setMobileAdditionalScreenPanel, setMobileMainScreenPanel } from '../../store/actions/main';
import { getByIds } from '../../store/keywordsList/actions';
import { setSortType as setInboxSortType } from '../../store/inboxes/actions';
import { activeCollectionSelector } from '../../store/selectors/collections';
import updateUser from '../../store/actions/user/updateUser';
import { formatProductAttributeTitle } from '../../store/helpers/productAttributes';
import * as UtilsCollections from '../../store/utils/collections';
import checkForCollectionsSortingsEquality from '../../helpers/checkForCollectionsSortingsEquality';
import { activeSortTypeSelector } from '../../store/inboxes/selectors';
import { updateSortType } from '../../store/actions/lightboards';
import { getAssets } from '../../store/actions/assets';
import Counter from '../Search/Counter';
import prepareDataForAppliedFilterEvent from './helpers/prepareDataForAppliedFilterEvent';
import {
  buildDefaultProductLayerFilter,
  getDefaultProductLayerIds,
  normalizeProductLayerOptions,
  restoreProductLayerFilterValue,
  serializeProductLayerForRoute,
} from './helpers/productLayerFilter';
import {
  ensurePinnedSearchFiltersOrder,
  getPinnedSearchFilterIds,
  isPinnedSearchFilter,
  sortPinnedFiltersFirst,
} from './helpers/pinnedSearchFilters';
import RangeSliderByImage from './RangeSlider/RangeSliderByImage';
import RangeSliderByText from './RangeSlider/RangeSliderByText';
import isVisualSearchEnabled from '../../helpers/isVisualSearchEnabled';

import './styles.scss';

const defaultSort = { type: 'uploadTime', order: 'desc' };
const booleanSearchOperators = [' OR ', ' AND ', ' NOT '];

const SearchFilters = ({ searchFilters = [], onlyTextSearch = false }) => {
  const navigate = useNavigate();
  const searchProps = getSearchProps();
  const dispatch = useDispatch();
  const assets = useSelector((state) => state.assets);
  const products = useSelector((state) => state.products);
  const user = useSelector((state) => state.user);
  const teamSalesChannels = useSelector((state) => state.user.team?.salesChannels || []);
  const CLIPEmbeddingsSimilarity = useSelector((state) => state.user?.team?.settings?.search?.CLIPEmbeddingsSimilarity || 0.2);
  const enableVisualSearchSetting = isVisualSearchEnabled(user);
  const teammates = useSelector((state) => state.teammates);
  const location = useSelector((state) => state.router.location);
  const openedTree = useSelector((state) => state.main.openedTree);
  const rootCollection = useSelector((state) => state.collections.collections?.my);
  const { activeLightboard } = useSelector((state) => state.lightboards);
  const [view, setView] = useState('');
  const FILTERS = useMemo(() => {
    if (!isRouteSearch(location.pathname) && !isRouteProducts(location.pathname) && !isRouteSelectedAssets(location.pathname)) return {};
    if (isRouteProducts(location.pathname)) {
      setView('products');
      return FILTER_NAMES_PRODUCTS;
    }
    setView('media');
    return FILTER_NAMES_ASSETS;
  }, [location.pathname]);

  const keywordsList = useSelector((state) => state.keywordsList.items);
  const treeKeywords = useSelector((state) => state.keywords.all);
  const savedSearches = useSelector((state) => state.savedSearches.all);
  const allFaces = useSelector((state) => state.faces.all);
  const activeCollections = useSelector(activeCollectionSelector);
  const collectionsSortType = useSelector((state) => state.collections.activeCollections.map((activeCollection) => activeCollection?.sortType));
  const lightboardsSortType = useSelector((state) => state.lightboards.activeLightboard?.sortType);
  const { inboxes, activeInboxID } = useSelector((state) => state.inboxes);
  const customFields = useSelector((state) => state.customFields.items);
  const productAttributes = useSelector((state) => state.productAttributes.items);
  const activeInboxSortType = useSelector(activeSortTypeSelector);
  const prevLocation = usePrevious(location);
  const prevCollectionsSortType = usePrevious(collectionsSortType[0]);
  const prevCollectionsSortTypeLength = usePrevious(collectionsSortType.length);
  const prevLightboardsSortType = usePrevious(lightboardsSortType);
  const prevInboxesSortType = usePrevious(activeInboxSortType);
  const watermarks = useSelector((state) => state.assets.watermarks);
  const [sortType, setSortType] = useState(defaultSort);
  const [isCollapsed, setIsCollapsed] = useState(utils.LocalStorage.get('picsio.searchFiltersMinimized'));
  const [hiddenItemsCount, setHiddenItemsCount] = useState(0);
  const [items, setItems] = useState([]);
  const [itemWidths, setItemWidths] = useState({});
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchByAsset, setSearchByAsset] = useState(null);
  const inputRef = useRef(null);
  const timeout = useRef();
  const [searchInputTarget, setSearchInputTarget] = useState(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setSearchInputTarget(document.getElementById('search-input'));
    }
  }, []);

  const assetThumbnail = assets.items.find((i) => i._id === searchProps?.byImageId)?.thumbnail;

  useEffect(() => {
    if (assetThumbnail && (!searchByAsset || searchByAsset._id !== searchProps?.byImageId)) {
      const asset = assets.items.find((i) => i._id === searchProps?.byImageId);
      setSearchByAsset(asset);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetThumbnail, searchByAsset, searchProps?.byImageId]);

  const availableSearchFilters = useMemo(() => {
    if (picsioConfig.isProofing) {
      return PROOFING_FILTERS;
    }
    if (isRouteProducts(location.pathname)) {
      return user.availableSearchFilters?.products || {};
    }
    const filterKeys = Object.keys(user.availableSearchFilters?.assets);
    const filteredAssets = filterKeys.filter((key) => user.availableSearchFilters?.assets[key]?.app !== 'toriut');
    const filteredAssetsObject = filteredAssets.reduce((acc, key) => {
      acc[key] = user.availableSearchFilters?.assets[key];
      return acc;
    }, {});
    return filteredAssetsObject;
  }, [location.pathname, user.availableSearchFilters?.assets, user.availableSearchFilters?.products]);

  const searchFiltersOrder = useMemo(() => {
    if (picsioConfig.isProofing) {
      return searchFilters;
    } if (isRouteProducts(location.pathname)) {
      return user?.settings?.favoriteSearchFilters?.products || [];
    }
    return user?.settings?.favoriteSearchFilters?.assets || [];
  }, [location.pathname, user?.settings?.favoriteSearchFilters?.products, user?.settings?.favoriteSearchFilters?.assets, searchFilters]);

  const pinnedFilterIds = useMemo(
    () => getPinnedSearchFilterIds(availableSearchFilters),
    [availableSearchFilters],
  );

  const [customizingMode, setCustomizingMode] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [confirmedFilters, setConfirmedFilters] = useState({});

  const [searchValue, setSearchValue] = useState('');

  const [open, setOpen] = useState('');

  const normalizeFieldOptions = useCallback((id, options) => {
    if (id === 'productLayer') {
      return normalizeProductLayerOptions(options, teamSalesChannels);
    }
    if (FILTERS[id]?.options) {
      return options.map((option) => ({ label: FILTERS[id].options[option], value: option }));
    }
    return options.map((option) => ({ label: option, value: option }));
  }, [FILTERS, teamSalesChannels]);

  const generateOperatorsOrder = useCallback((operators, field, id) => {
    if (OPERATORS_ORDER[id]) {
      const common = _intersection([...OPERATORS_ORDER[id]], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER[id]]);
      return [...common, ...rest];
    }
    if (field.type === 'date') {
      const common = _intersection([...OPERATORS_ORDER.date], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.date]);
      return [...common, ...rest];
    }
    if (field.type === 'number') {
      const common = _intersection([...OPERATORS_ORDER.number], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.number]);
      return [...common, ...rest];
    }
    if (field.type === 'enum' && field.multiple) {
      const common = _intersection([...OPERATORS_ORDER.enumMultiple], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.enumMultiple]);
      return [...common, ...rest];
    }
    if (field.type === 'enum' && !field.multiple) {
      const common = _intersection([...OPERATORS_ORDER.enumSingle], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.enumSingle]);
      return [...common, ...rest];
    }
    if (id === 'comment' && field.type === 'string') {
      const common = _intersection([...OPERATORS_ORDER.comment], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.comment]);
      return [...common, ...rest];
    }
    if (field.type === 'string' || field.type === 'text') {
      const common = _intersection([...OPERATORS_ORDER.text], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.text]);
      return [...common, ...rest];
    }
    return operators;
  }, []);

  const normalizeDefaultSearchFields = useCallback((fields) => fields.filter(([, { hidden }]) => !hidden).map(([id, filed]) => {
    const isProductsView = isRouteProducts(location.pathname);
    let fieldTitle = FILTERS[id]?.title || id;
    if (isProductsView && id.startsWith('meta.')) {
      fieldTitle = formatProductAttributeTitle(id.replace('meta.', ''));
    }
    const fieldData = {
      ...filed,
      operators: generateOperatorsOrder(filed.operators, filed, id),
      id,
      title: fieldTitle,
    };
    if (filed.options) {
      fieldData.options = normalizeFieldOptions(id, filed.options);
    }

    if (filed.type === 'boolean') {
      fieldData.options = BOOL_OPTIONS;
    }

    if (filed.type === 'productLayer') {
      fieldData.operators = ['oneOf'];
    }

    if (FILTERS[id]?.booleanOptions) {
      fieldData.options = FILTERS[id].booleanOptions;
    }
    return fieldData;
  }), [FILTERS, normalizeFieldOptions, location.pathname, location.key, generateOperatorsOrder]);

  const initFilters = useMemo(
    () => normalizeDefaultSearchFields(Object.entries(availableSearchFilters)),
    [availableSearchFilters, normalizeDefaultSearchFields, location.pathname, location.key],
  );

  const [activeFilters, setActiveFilters] = useState(initFilters);
  const [order, setOrder] = useState(() => ensurePinnedSearchFiltersOrder(
    searchFiltersOrder?.length ? searchFiltersOrder : initFilters.map(({ id }) => id),
    pinnedFilterIds,
    { pinAtStart: isRouteProducts(location.pathname) },
  ));
  const [prevFilters, setPrevFilters] = useState(activeFilters);
  const inactiveFilters = useMemo(
    () => activeFilters.filter(({ id }) => !order?.find((item) => item === id)
      && !isPinnedSearchFilter(id, pinnedFilterIds)),
    [order, activeFilters, pinnedFilterIds],
  );

  const [prevOrder, setPrevOrder] = useState(order);

  useEffect(() => {
    if (!pinnedFilterIds.length) return;
    setOrder((prev) => ensurePinnedSearchFiltersOrder(
      searchFiltersOrder.length ? searchFiltersOrder : prev,
      pinnedFilterIds,
      { pinAtStart: isRouteProducts(location.pathname) },
    ));
  }, [searchFiltersOrder, pinnedFilterIds, location.pathname]);

  const filterIsHidden = useCallback((entity) => {
    if (entity === 'lightboardId' || entity === 'inboxId') return true;
    if (entity === 'collectionIds' && activeCollections.length === 1 && activeCollections[0]?._id === rootCollection?._id) return true;
    return false;
  }, [activeCollections, rootCollection?._id]);

  const resetIsHidden = useMemo(() => {
    const filters = { ...confirmedFilters };
    delete filters.text;
    delete filters.byImageId;
    delete filters.similarityThreshold;
    delete filters.collectionIdsOperator;
    delete filters.searchSource;

    if (!Object.keys(filters).length) return true;

    if (Object.keys(filters).length === 1) {
      if (filters.collectionIds && filters.collectionIds?.value?.length === 1 && filters.collectionIds.value[0]._id === rootCollection?._id) return true;
      if (filters.lightboardId) return true;
      if (filters.inboxId) return true;
      return false;
    }
    return false;
  }, [rootCollection?._id, confirmedFilters]);

  const sortFilters = useCallback((withNew = false) => {
    const sorted = activeFilters.reduce((acc, filter) => {
      const index = order.findIndex((item) => item === filter.id);
      if (index > -1) {
        acc.byOrder[index] = filter;
      } else if (withNew) {
        acc.new.push(filter);
      }
      return acc;
    }, { byOrder: [], new: [] });

    const result = [...sorted.byOrder.filter(Boolean), ...sorted.new];

    const newOrder = result.map((filter) => filter.id);
    if (!_isEqual(newOrder, order)) {
      setOrder(newOrder);
    }

    return result;
  }, [activeFilters, order]);

  const renderItems = useMemo(() => {
    const items = sortFilters();
    return sortPinnedFiltersFirst(items, pinnedFilterIds);
  }, [sortFilters, pinnedFilterIds]);

  const itemsRef = useRef([]);
  itemsRef.current = renderItems.map((_, i) => itemsRef.current[i] || React.createRef());
  const containerRef = useRef(null);
  const controlsRef = useRef(null);
  const hiddenItemsCounterRef = useRef(null);
  const internalSubmit = useRef(false);
  const searchSource = useRef('searchbar');
  const searchTagIds = useRef(null);
  const searchInboxId = useRef(null);
  const searchLightboardId = useRef(null);

  const originalEl = useRef();
  const draggableElement = useRef();
  const tmpItem = useRef();
  const tmpPosition = useRef(null);
  const itemToMove = useRef('');
  const itemToMoveAfter = useRef('');

  const templateTmpItem = useRef(document.createElement('div'));
  templateTmpItem.current.classList.add('temporarySearchFilterItem');

  const onRouteSearch = () => {
    searchTagIds.current = searchProps.collectionIds;
    searchLightboardId.current = searchProps.lightboardId;
    searchInboxId.current = searchProps.inboxId;
    let searchOrder = searchFiltersOrder;

    const nonExistingFilters = Object.keys(searchProps).filter((entity) => !(searchFiltersOrder || [])?.includes(entity) && !filterIsHidden(entity));
    if (nonExistingFilters.length > 0) {
      searchOrder = [...order, ...nonExistingFilters];
    }

    if (internalSubmit.current) {
      internalSubmit.current = false;
      return;
    }

    const result = { text: '' };
    Object.entries(searchProps).forEach(([key, value]) => {
      if (key.endsWith('Operator')) {
        const id = key.replace(/Operator/ig, '');
        if (result[id]) {
          result[id].operator = value;
        } else {
          result[id] = { operator: value };
        }
      } else if (key === 'params2') {
        result.params2 = value;
      } else if (result[key]) result[key].value = value;
      else result[key] = { value };
    });

    const metaFields = Object.keys(searchProps).filter((searchFilter) => searchFilter.startsWith('meta.') && !searchFilter.includes('Operator'));
    const metaSchemaFields = isRouteProducts(location.pathname) ? productAttributes : customFields;
    if (metaFields) {
      metaSchemaFields.forEach((customField) => {
        const customFieldTitle = `meta.${customField.title}`;
        const isMultipleOperator = MULTIPLE_OPERATORS.includes(result[customFieldTitle]?.operator);
        if (metaFields.includes(customFieldTitle) && customField.type === 'enum' && searchProps[customFieldTitle]) {
          if (Array.isArray(searchProps[customFieldTitle])) {
            result[customFieldTitle].value = searchProps[customFieldTitle].map((option) => ({ title: option, value: option, _id: option }));
          } else {
            result[customFieldTitle].value = [{ title: searchProps[customFieldTitle], value: searchProps[customFieldTitle], _id: searchProps[customFieldTitle] }];
          }
        }
        if (isMultipleOperator && searchProps.params2 && searchProps.params2[customFieldTitle]) {
          searchProps.params2[customFieldTitle].forEach((option) => {
            const item = { title: option, value: option, _id: option };
            if (result[customFieldTitle]?.excludedValues) {
              result[customFieldTitle].excludedValues.push(item);
            } else {
              result[customFieldTitle].excludedValues = [item];
            }
          });
        }
      });
    }

    const confirmedUsers = teammates.items.filter((teammate) => teammate.parent === undefined || teammate.parent.confirmed === true);
    if (searchProps?.commentedBy || searchProps.params2?.commentedBy) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.commentedBy?.operator);
      if (searchProps?.commentedBy) {
        if (Array.isArray(searchProps?.commentedBy)) {
          result.commentedBy.value = confirmedUsers.filter((teammate) => searchProps?.commentedBy?.includes(teammate._id));
          if (isMultipleOperator) {
            result.commentedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.commentedBy?.includes(teammate._id));
          }
        } else {
          result.commentedBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.commentedBy)];
          if (isMultipleOperator) {
            result.commentedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.commentedBy)];
          }
        }
      }

      if (isMultipleOperator && searchProps?.params2?.commentedBy) {
        if (Array.isArray(searchProps?.params2?.commentedBy)) {
          result.commentedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.commentedBy?.includes(teammate._id));
        } else {
          result.commentedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.commentedBy)];
        }
      }
    }

    if (searchProps?.products) {
      if (Array.isArray(searchProps?.products)) {
        result.products.value = products.items.filter((product) => searchProps?.products?.includes(product._id));
      } else {
        result.products.value = [products.items.find((product) => product._id === searchProps?.products)];
      }
    }

    if (searchProps?.assignees || searchProps?.params2?.assignees) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.assignees?.operator);
      if (searchProps?.assignees) {
        if (Array.isArray(searchProps?.assignees)) {
          result.assignees.value = confirmedUsers.filter((teammate) => searchProps?.assignees?.includes(teammate._id));
        } else {
          result.assignees.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.assignees)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.assignees) {
        if (Array.isArray(searchProps?.params2?.assignees)) {
          result.assignees.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.assignees?.includes(teammate._id));
        } else {
          result.assignees.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.assignees)];
        }
      }
    }

    if (searchProps?.approvedBy || searchProps?.params2?.approvedBy) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.approvedBy?.operator);
      if (searchProps?.approvedBy) {
        if (Array.isArray(searchProps?.approvedBy)) {
          result.approvedBy.value = confirmedUsers.filter((teammate) => searchProps?.approvedBy?.includes(teammate._id));
        } else {
          result.approvedBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.approvedBy)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.approvedBy) {
        if (Array.isArray(searchProps?.params2?.approvedBy)) {
          result.approvedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.approvedBy?.includes(teammate._id));
        } else {
          result.approvedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.approvedBy)];
        }
      }
    }

    if (searchProps.createdBy) {
      if (Array.isArray(searchProps.createdBy)) {
        result.createdBy.value = confirmedUsers.filter((teammate) => searchProps.createdBy.includes(teammate._id));
      } else {
        result.createdBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps.createdBy)];
      }
    }

    if ((searchProps?.keywords && !SINGLE_OPERATORS.includes(result?.keywords?.operator)) || searchProps?.params2?.keywords) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.keywords?.operator);
      result.keywords.value = [];
      if (isMultipleOperator) result.keywords.excludedValues = [];
      const notFoundInTheStore = [];
      searchProps?.keywords?.forEach((id) => {
        const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
        if (keyword) {
          const item = {
            _id: keyword._id,
            title: keyword.path.split('→').pop().toLowerCase(),
          };
          result.keywords.value.push(item);
        } else {
          notFoundInTheStore.push(id);
        }
      });

      if (isMultipleOperator) {
        searchProps?.params2?.keywords?.forEach((id) => {
          const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
          if (keyword) {
            const item = {
              _id: keyword._id,
              title: keyword.path.split('→').pop().toLowerCase(),
            };
            result.keywords.excludedValues.push(item);
          } else {
            notFoundInTheStore.push(id);
          }
        });
      }

      if (notFoundInTheStore.length) {
        (async () => {
          try {
            const { payload } = await dispatch(getByIds(notFoundInTheStore));
            if (Array.isArray(payload)) {
              const additionalValue = payload
                .filter(({ _id }) => searchProps.keywords.includes(_id))
                .map(({ _id, name }) => ({ _id, title: name }));

              const additionalExcluded = isMultipleOperator
                ? payload
                  .filter(({ _id }) => searchProps?.params2?.keywords?.includes(_id))
                  .map(({ _id, name }) => ({ _id, title: name }))
                : [];

              result.keywords = {
                ...(result.keywords || {}),
                value: [...(result.keywords.value || []), ...additionalValue],
                ...(isMultipleOperator
                  ? { excludedValues: [...(result.keywords.excludedValues || []), ...additionalExcluded] }
                  : {}),
              };
            }
          } catch (error) {
            Logger.error(new Error('Can not get keywords by IDS on search filters'), { error, ids: notFoundInTheStore }, [
              'GetKeywordsByIdsFailedOnSearchFilters',
              { errorMessage: (error && error.message) || 'NoMessage' },
            ]);
          }
        })();
      }
    }

    if (searchProps.inboxId) {
      result.inboxId.value = [inboxes.find((inbox) => inbox._id === activeInboxID)];
    }

    if (searchProps.collectionIds) {
      result.collectionIds.value = activeCollections;
    }

    if (searchProps?.watermarkId || searchProps?.params2?.watermarkId) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.watermarkId?.operator);
      if (searchProps?.watermarkId) {
        if (Array.isArray(searchProps.watermarkId)) {
          result.watermarkId.value = watermarks.filter((watermark) => searchProps.watermarkId.includes(watermark._id));
        } else {
          result.watermarkId.value = [watermarks.find((watermark) => watermark._id === searchProps.watermarkId)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.watermarkId) {
        if (Array.isArray(searchProps?.params2?.watermarkId)) {
          result.watermarkId.excludedValues = watermarks.filter((watermark) => searchProps.params2.watermarkId.includes(watermark._id));
        } else {
          result.watermarkId.excludedValues = [watermarks.find((watermark) => watermark._id === searchProps.params2.watermarkId)];
        }
      }
    }
    if (searchProps.mimeType && searchProps.mimeTypeOperator === 'oneOf') {
      const mimeTypes = Object.keys(MIME_TYPES).reduce((acc, key) => [...acc, ...MIME_TYPES[key]], []);
      const convertedMimeTypes = mimeTypes.map((item) => ({
        title: item, value: item, _id: item,
      }));
      if (Array.isArray(searchProps.mimeType)) {
        result.mimeType.value = searchProps.mimeType
          .map((mimeType) => convertedMimeTypes.find((converted) => converted._id === mimeType))
          .filter(Boolean); // Remove any undefined values
      } else {
        // Single value case
        const found = convertedMimeTypes.find((mimeType) => mimeType._id === searchProps.mimeType);
        result.mimeType.value = found ? [found] : [];
      }
    }

    if (searchProps.keywordsAttachedBy) {
      const optionMap = FILTER_NAMES_ASSETS.keywordsAttachedBy.options;
      const convertedOptions = Object.entries(optionMap).map(([value, title]) => ({ title, value, _id: value }));
      const raw = Array.isArray(searchProps.keywordsAttachedBy)
        ? searchProps.keywordsAttachedBy
        : [searchProps.keywordsAttachedBy];
      result.keywordsAttachedBy.value = raw
        .map((v) => convertedOptions.find((opt) => opt._id === v))
        .filter(Boolean);
    }

    if (searchProps?.faces || searchProps?.params2?.faces) {
      result.faces.value = [];
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.faces?.operator);
      if (isMultipleOperator) result.faces.excludedValues = [];

      searchProps?.faces?.forEach((id) => {
        const face = allFaces.find((face) => face._id === id);
        if (face) {
          const item = {
            _id: face._id,
            title: face.name.split('→').pop(),
          };
          result.faces.value.push(item);
        }
      });
      if (isMultipleOperator) {
        searchProps?.params2?.faces?.forEach((id) => {
          const face = allFaces.find((face) => face._id === id);
          if (face) {
            const item = {
              _id: face._id,
              title: face.name.split('→').pop().toLowerCase(),
            };
            result.faces.excludedValues.push(item);
          }
        });
      }
    }

    if (searchProps.searchSource) {
      result.searchSource = searchProps.searchSource;
    }

    if (searchProps.lightboardId) {
      result.lightboardId.value = [activeLightboard];
    }

    const defaultProductLayerIds = getDefaultProductLayerIds(availableSearchFilters.productLayer?.options);
    const productLayerValue = searchProps?.productLayer
      ?? (isRouteProducts(location.pathname) ? defaultProductLayerIds : undefined);

    if (productLayerValue && availableSearchFilters.productLayer) {
      const layers = Array.isArray(productLayerValue) ? productLayerValue : [productLayerValue];
      const rawOptions = availableSearchFilters.productLayer?.options || layers;
      const normalizedOptions = normalizeProductLayerOptions(rawOptions, teamSalesChannels);

      result.productLayer = {
        operator: 'oneOf',
        value: restoreProductLayerFilterValue(layers, normalizedOptions),
      };
    }

    if (searchProps.text) {
      result.text = searchProps.text;
    }
    Object.entries(result).forEach(([id, { operator, value }]) => {
      if (!operator) {
        if (id === 'collectionIds' || id === 'keywords' || id === 'faces') {
          result[id].operator = 'oneOf';
        } else if (id === 'lightboardId' || id === 'inboxId' || id === 'trashed') {
          result[id].operator = 'equals';
        } else if (availableSearchFilters[id]) {
          if (availableSearchFilters[id].type === 'date' && SINGLE_OPERATORS.includes(value)) {
            result[id].operator = value;
            result[id].value = true;
          } else {
            result[id].operator = availableSearchFilters[id]?.defaultOperator;
          }
        }
      }
    });
    setSearchValue(result.text);
    setSelectedFilters(result);
    setConfirmedFilters(result);
    setActiveFilters(initFilters);
    setOrder(ensurePinnedSearchFiltersOrder(
      searchOrder,
      pinnedFilterIds,
      { pinAtStart: isRouteProducts(location.pathname) },
    ));
  };

  const makeSearchRoute = useCallback((filters) => {
    const { archived } = searchProps;
    const rootCollectionId = rootCollection?._id;
    const multiValueKeys = new Set([
      'faces',
      'collectionIds',
      'lightboardId',
      'inboxId',
      'keywords',
      'assignees',
      'commentedBy',
      'createdBy',
      'watermarkId',
      'mimeType',
      'approvedBy',
      'products',
      'byImageId',
      'similarityThreshold',
      'keywordsAttachedBy',
    ]);

    const result = Object.entries(filters).reduce((acc, [id, { operator, value, excludedValues }]) => {
      if (id === 'productLayer') {
        acc[id] = serializeProductLayerForRoute(value);
        return acc;
      }

      const isMulti = multiValueKeys.has(id) || id.startsWith('meta.');

      if (isMulti) {
        acc[id] = Array.isArray(value) && value.length > 0
          ? value.map((item) => item?._id ?? item)
          : value;

        if (Array.isArray(excludedValues) && excludedValues.length > 0) {
          acc.params2 = {
            ...acc.params2,
            [id]: excludedValues.map((item) => item?._id ?? item),
          };
        }

        acc[`${id}Operator`] = operator;
      } else if (id !== 'params2') {
        acc[id] = value;
        acc[`${id}Operator`] = operator;
      }

      return acc;
    }, {});

    if (archived && searchSource.current) {
      result.archived = archived;
    }
    if (filters.text) result.text = filters.text;

    // eslint-disable-next-line no-param-reassign
    delete filters.searchSource;
    if (searchSource.current && !_isEmpty(filters)) result.searchSource = searchSource.current;

    if (location?.query?.archived && filters.collection?.value && filters.collection?.value.length > 0) {
      delete result.archived;
    }
    if (location?.query?.trashed && filters.collection?.value && (!filters.collection?.value.includes(rootCollectionId) || filters.collection?.value.length > 1)) {
      delete result.trashed;
    }
    if (openedTree && openedTree !== 'collections' && filters.collection?.value && filters.collection?.value.length > 0) {
      dispatch(changeTree('collections'));
    }
    if (Object.keys(filters).length === 0 && Object.keys(result).length === 0 && view === 'media') {
      result.collectionIds = rootCollectionId;
    }

    return result;
  }, [dispatch, location?.query?.archived, location?.query?.trashed, openedTree, rootCollection?._id, view, searchProps]);

  const setRoute = useCallback((filters) => {
    const isMobile = ua.browser.isNotDesktop() || ua.isMobileApp();
    internalSubmit.current = true;

    // for submit or reset, when route not changed
    setTimeout(() => {
      internalSubmit.current = false;
    }, 200);
    setSearchRoute(makeSearchRoute(filters), { isProducts: view === 'products' });

    if (isMobile) {
      dispatch(setMobileAdditionalScreenPanel());
      dispatch(setMobileMainScreenPanel('catalog'));
    }
  }, [dispatch, makeSearchRoute, view]);

  const submit = useCallback((filters, isReset = false) => {
    searchSource.current = isReset ? null : 'searchbar';
    setRoute(filters);
  }, [setRoute]);

  const handleSelectFilter = useCallback((id, data) => {
    setSelectedFilters((prev) => ({ ...prev, [id]: data }));
  }, []);

  const isEntity = useCallback((id) => {
    if (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId') {
      return true;
    } return false;
  }, []);

  const getEntity = useCallback((filters) => Object.keys(filters).find((filter) => isEntity(filter)), [isEntity]);

  const handleConfirmFilter = useCallback((id, data, closeFilter = true) => {
    if (FILTER_NAMES_ASSETS[id]?.title === FILTER_NAMES_ASSETS.content.title) {
      Logger.log('User', 'DocumentTextFilterUsed');
    }

    setConfirmedFilters((prev) => {
      const oldFilters = { ...prev };
      if (!oldFilters[id] && isEntity(id)) {
        const oldSearchEntity = getEntity(oldFilters);
        if (oldSearchEntity === 'collectionIds') {
          searchTagIds.current = null;
        } else if (oldSearchEntity === 'inboxId') {
          searchInboxId.current = null;
        } else if (oldSearchEntity === 'lightboardId') {
          searchLightboardId.current = null;
        }
        delete oldFilters[oldSearchEntity];
      }
      const nextFilters = {
        ...oldFilters,
        [id]: data,
      };
      if (searchValue) {
        nextFilters.text = searchValue;
      }

      Logger.log('User', 'FilterApplied', {
        filter_name: id,
        filter_operator: data.operator,
        filter_value: data.value,
        active_filters_list: prepareDataForAppliedFilterEvent(nextFilters).join(', '),
        active_filters_uniqueIDs: prepareDataForAppliedFilterEvent(nextFilters, true).join(', '),
        active_filters_count: prepareDataForAppliedFilterEvent(nextFilters).length,
        broad_search_value: searchValue,
      });

      submit(nextFilters);
      return nextFilters;
    });
    if (closeFilter) setOpen('');
  }, [getEntity, isEntity, searchValue, submit]);

  const getDefaultProductLayerFilter = useCallback(() => (
    buildDefaultProductLayerFilter(
      availableSearchFilters.productLayer?.options,
      teamSalesChannels,
    )
  ), [availableSearchFilters.productLayer?.options, teamSalesChannels]);

  const handleResetAllFilters = useCallback((e, initiatedFrom) => {
    Logger.log('User', 'ResetAllFiltersClicked', initiatedFrom);
    setSearchValue('');
    if (searchInboxId.current) {
      setConfirmedFilters({ inboxId: confirmedFilters.inboxId });
      setSelectedFilters({ inboxId: selectedFilters.inboxId });
      submit({}, true);
      return;
    }
    if (searchLightboardId.current) {
      setConfirmedFilters({ lightboardId: confirmedFilters.lightboardId });
      setSelectedFilters({ lightboardId: selectedFilters.lightboardId });
      submit({}, true);
      return;
    }
    if (searchTagIds.current) {
      setConfirmedFilters({
        collectionIds: {
          operator: 'oneOf',
          value: [rootCollection],
        },
        byImageId: { value: searchProps?.byImageId },
        similarityThreshold: { value: searchProps?.similarityThreshold },
      });
      setSelectedFilters({
        collectionIds: {
          operator: 'oneOf',
          value: [rootCollection],
        },
        byImageId: { value: searchProps?.byImageId },
        similarityThreshold: { value: searchProps?.similarityThreshold },
      });
      submit({
        collectionIds: {
          operator: 'oneOf',
          value: [rootCollection],
        },
        byImageId: { value: searchProps?.byImageId },
        similarityThreshold: { value: searchProps?.similarityThreshold },
      }, true);
      return;
    }
    if (view === 'products' && availableSearchFilters.productLayer) {
      const defaultProductLayer = getDefaultProductLayerFilter();
      setSelectedFilters({ productLayer: defaultProductLayer });
      setConfirmedFilters({ productLayer: defaultProductLayer });
      setOpen('');
      submit({ productLayer: defaultProductLayer }, true);
      return;
    }
    setSelectedFilters({});
    setConfirmedFilters({});
    setOpen('');
    submit({
      collectionIds: {
        operator: 'oneOf',
        value: [rootCollection],
      },
      byImageId: { value: searchProps?.byImageId },
      similarityThreshold: { value: searchProps?.similarityThreshold },
    }, true);
  }, [
    availableSearchFilters.productLayer,
    confirmedFilters.inboxId,
    confirmedFilters.lightboardId,
    getDefaultProductLayerFilter,
    rootCollection,
    searchProps?.byImageId,
    searchProps?.similarityThreshold,
    selectedFilters.inboxId,
    selectedFilters.lightboardId,
    submit,
    view,
  ]);

  const handleResetFilter = useCallback((id) => {
    const resetCollectionFilter = (filters) => ({
      ...filters,
      collectionIds: {
        operator: 'oneOf',
        value: [rootCollection],
      },
    });
    const resetProductLayerFilter = (filters) => ({
      ...filters,
      productLayer: getDefaultProductLayerFilter(),
    });
    const shouldResetProductLayer = id === 'productLayer'
      && view === 'products'
      && availableSearchFilters.productLayer;

    setSelectedFilters((prev) => {
      const nextFilters = { ...prev };
      if (shouldResetProductLayer) {
        return resetProductLayerFilter(nextFilters);
      }
      delete nextFilters[id];
      return (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId')
        ? resetCollectionFilter(nextFilters)
        : nextFilters;
    });

    setConfirmedFilters((prev) => {
      const nextFilters = { ...prev };
      Logger.log('User', 'ResetFilterClicked', {
        filter_name: id,
        filter_operator: nextFilters[id]?.operator || 'none',
        filter_value: nextFilters[id]?.value || 'none',
        remaining_filters_list: Object.keys(nextFilters).filter((key) => key !== id),
        remaining_filters_count: Object.keys(nextFilters).length - 1,
      });
      let updatedFilters = nextFilters;
      if (shouldResetProductLayer) {
        updatedFilters = resetProductLayerFilter(nextFilters);
      } else {
        delete nextFilters[id];
        updatedFilters = (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId')
          ? resetCollectionFilter(nextFilters)
          : nextFilters;
      }
      submit(updatedFilters);
      return updatedFilters;
    });

    setOpen('');
  }, [
    availableSearchFilters.productLayer,
    getDefaultProductLayerFilter,
    rootCollection,
    submit,
    view,
  ]);

  const handleCustomizingMode = useCallback(() => {
    Logger.log('User', 'ManageFiltersClicked');
    setCustomizingMode((prev) => !prev);
    setPrevFilters(activeFilters);
    setPrevOrder(order);
  }, [activeFilters, order]);

  const handleSavedSearchSave = useCallback(() => {
    showSaveSearchDialog({
      user,
      searchData: makeSearchRoute(confirmedFilters),
      entity: picsioConfig.isPim && view === 'products' ? 'products' : undefined,
      searchValue,
      confirmedFilters,
    });
  }, [confirmedFilters, makeSearchRoute, searchValue, user, view]);

  const handleCancelCustomizingMode = useCallback(() => {
    Logger.log('User', 'CancelFilterConfigurationClicked');
    setCustomizingMode(false);
    setActiveFilters(prevFilters);
    setOrder(prevOrder);
  }, [prevFilters, prevOrder]);

  const handleSaveFilters = useCallback(() => {
    Logger.log('User', 'SaveFilterConfigurationClicked');
    const isFiltersOrderChanged = !_isEqual(order, prevOrder);
    if (isFiltersOrderChanged) {
      Logger.log('User', 'ChangeFilterDisplayOrder', {
        changed_filter_position: order,
      });
      recalculateItems();
    }
    setCustomizingMode(false);
    setPrevFilters(activeFilters);
    setPrevOrder(order);
    if (picsioConfig.isPim) {
      if (view === 'media') {
        dispatch(updateUser({
          settings: {
            favoriteSearchFilters: {
              assets: order,
              products: [...(user.settings?.favoriteSearchFilters?.products || [])],
            },
          },
        }, false));
      }
      if (view === 'products') {
        const productsOrder = ensurePinnedSearchFiltersOrder(
          order,
          pinnedFilterIds,
          { pinAtStart: true },
        );
        dispatch(updateUser({
          settings: {
            favoriteSearchFilters: {
              products: productsOrder,
              assets: [...(user.settings?.favoriteSearchFilters?.assets || [])],
            },
          },
        }, false));
      }
    } else {
      dispatch(updateUser({
        settings: {
          favoriteSearchFilters: {
            assets: order,
          },
        },
      }, false));
    }
  }, [order,
    prevOrder,
    activeFilters,
    view,
    recalculateItems,
    dispatch,
    user?.settings?.favoriteSearchFilters?.products,
    user?.settings?.favoriteSearchFilters?.assets]);

  const checkActive = useCallback((id) => Boolean(confirmedFilters[id]), [confirmedFilters]);

  const checkOpen = useCallback((id) => id === open, [open]);

  const handleOpen = useCallback(
    (id) => {
      if (customizingMode) return;
      setOpen(id);
    }, [customizingMode],
  );

  const handleClose = useCallback(
    (e) => {
      e.stopPropagation();
      if (customizingMode) return;
      setOpen('');
    }, [customizingMode],
  );

  const handleRemoveFilter = useCallback((id) => {
    if (isPinnedSearchFilter(id, pinnedFilterIds)) return;
    Logger.log('User', 'RemoveFilterClicked', {
      removed_filter: id,
    });
    setOrder(order.filter((or) => or !== id));
  }, [order, pinnedFilterIds]);

  const handleAddFilters = useCallback((filters) => {
    const filtersIds = filters.map(({ id }) => id);
    Logger.log('User', 'AddFiltersClicked', {
      selected_filter_name: filtersIds,
    });
    setOrder([...order, ...filtersIds]);
  }, [order]);

  const handleShowAddFiltersDialog = useCallback(() => {
    showAddNewFiltersDialog({ filters: inactiveFilters, onOk: handleAddFilters });
  }, [handleAddFilters, inactiveFilters]);

  const preventDefault = (event) => {
    event.preventDefault();
  };

  const dragstart = (event) => {
    document.body.addEventListener('dragover', preventDefault);
    document.body.addEventListener('drop', preventDefault);

    event.stopPropagation();

    const el = (originalEl.current = event.currentTarget);
    draggableElement.current = event.currentTarget;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', 'Data to Drag');
    tmpItem.current = templateTmpItem.current;

    tmpPosition.current = itemToMove.current = itemToMoveAfter.current = event.currentTarget.dataset.id;

    setTimeout(() => {
      el.parentNode.insertBefore(tmpItem.current, el);
      el.style.display = 'none';
    }, 0);
  };

  const dragenter = (event) => {
    if (!draggableElement.current) return;
    event.stopPropagation();
    const { currentTarget } = event;
    const { id } = currentTarget.dataset;
    const el = currentTarget;
    el.after(tmpItem.current);

    tmpPosition.current = itemToMoveAfter.current = id;
  };

  const dragend = () => {
    document.body.removeEventListener('dragover', preventDefault);
    document.body.removeEventListener('drop', preventDefault);
    tmpItem.current.remove();
    draggableElement.current = null;
    originalEl.current.style.display = 'inline-block';
    if (itemToMoveAfter.current === 'first') {
      itemToMoveAfter.current = '';
    }

    const fieldToMove = activeFilters.find((item) => item.id === itemToMove.current);
    const fieldToMoveAfter = activeFilters.find(
      (item) => item.id === itemToMoveAfter.current,
    );

    if (!fieldToMove || fieldToMove === fieldToMoveAfter) return;
    if (isPinnedSearchFilter(fieldToMove.id, pinnedFilterIds)) return;

    const fieldsToMove = [fieldToMove.id];
    const currentFieldToMoveAfter = itemToMoveAfter.current;

    const newItems = [...order];
    const indexToRemove = newItems.findIndex((item) => item === fieldsToMove[0]);
    const itemsToMove = newItems.splice(indexToRemove, fieldsToMove.length);
    const indexToPut = currentFieldToMoveAfter ? newItems.findIndex((item) => item === currentFieldToMoveAfter) : -1;
    newItems.splice(indexToPut + 1, 0, ...itemsToMove);
    setOrder(ensurePinnedSearchFiltersOrder(
      newItems,
      pinnedFilterIds,
      { pinAtStart: isRouteProducts(location.pathname) },
    ));
  };

  const getCurrentSortType = () => {
    const { collectionIds, lightboardId, inboxId } = location.query;
    let sortTypeNew = sortType;

    if (collectionIds && collectionsSortType.length) {
      if (collectionsSortType.length === 1) {
        sortTypeNew = collectionsSortType[0];
      } else {
        const equalSortings = checkForCollectionsSortingsEquality(collectionsSortType);
        if (equalSortings) {
          sortTypeNew = collectionsSortType[0];
        } else {
          sortTypeNew = defaultSort;
        }
      }
    } else if (lightboardId && lightboardsSortType) {
      sortTypeNew = lightboardsSortType;
    } else if (inboxId) {
      if (activeInboxSortType) {
        sortTypeNew = activeInboxSortType;
      }
    }

    return sortTypeNew;
  };

  const changeSortType = () => {
    const sortTypeNew = getCurrentSortType();
    if (sortTypeNew !== sortType) {
      setSortType(sortTypeNew);
    }
  };

  useEffect(() => {
    if ((isRouteSearch() && prevLocation !== location) || (
      prevCollectionsSortType !== collectionsSortType[0]
      || prevCollectionsSortTypeLength !== collectionsSortType.length
      || prevLightboardsSortType !== lightboardsSortType
      || prevInboxesSortType !== activeInboxSortType
    )) {
      changeSortType();
    }
  });

  const handleChangeSort = useCallback(async (name, order) => {
    if (picsioConfig.isMainApp) {
      const { collectionIds, lightboardId, inboxId } = location.query;
      if (collectionIds) {
        UtilsCollections.setSortType({
          type: name,
          order,
        });
      }

      if (inboxId) {
        await dispatch(setInboxSortType({ _id: inboxId, sortType: { type: name, order } }));
        reloadRoute();
      }

      if (lightboardId) {
        dispatch(updateSortType(lightboardId, {
          type: name,
          order,
        }));
      }
    } else {
      const newSortType = { type: name, order };
      setSortType(newSortType);
      window.websiteConfig.sortType = newSortType;
      dispatch(getAssets(true));
    }
  }, [dispatch, location.query]);

  useEffect(() => {
    onRouteSearch();
  }, [activeCollections, allFaces, location.key, activeLightboard, activeInboxID]);

  const getSearchPlaceholder = useMemo(() => {
    const {
      lightboardId, inboxId, archived, collectionIds, trashed,
    } = searchProps;

    let placeholder = 'Search all collections';

    if (picsioConfig.isPim) {
      placeholder = 'Type to search';
      return placeholder;
    }
    const rootCollectionId = rootCollection?._id;

    // archive
    if (collectionIds && archived) {
      if (activeCollections?.name && activeCollections?.name !== rootCollection.name) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections.name)}'`;
      }
    } else if (trashed && activeCollections.every(({ _id }) => _id === rootCollectionId)) {
      placeholder = 'Search in trashed assets';
    } else if (collectionIds) {
      if (activeCollections.length === 1 && activeCollections[0]?.name && activeCollections[0]?.name !== rootCollection.name) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections[0].name)}'`;
      } else if ((activeCollections.length === 1 && activeCollections.find(({ _id }) => _id !== rootCollectionId))) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections[0].name)}'`;
      } else if (activeCollections.length > 1) {
        placeholder = 'Search in multiple collections';
      }
    } else if (lightboardId) {
      if (activeLightboard?.path) {
        const name = activeLightboard.path.split('→').pop();
        placeholder = `Search in '${decodeURIComponent(name)}'`;
      }
    } else if (inboxId) {
      const inbox = inboxes.find((item) => item._id === inboxId) || {};
      if (inbox?.name) {
        const { name } = inbox;
        placeholder = `Search in '${decodeURIComponent(name)}'`;
      }
    }
    return placeholder;
  }, [activeCollections, activeLightboard, inboxes, rootCollection?._id, rootCollection?.name, location.key, assets.items, searchProps]);

  const handleSearchValueChange = useCallback((searchText) => {
    setSearchValue(searchText);
  }, []);

  const transformSuggestTextToFilters = useCallback((suggestion) => ({
    ...confirmedFilters,
    ...suggestion.filter,
  }), [confirmedFilters]);

  const submitSearchValue = async (text, suggestion) => {
    if (suggestion?.filter) {
      const data = transformSuggestTextToFilters(suggestion);
      setSearchRoute(makeSearchRoute({
        ...data,
        similarityThreshold: enableVisualSearchSetting ? { value: searchProps?.similarityThreshold || Number(CLIPEmbeddingsSimilarity).toFixed(2) } : '',
      }), { isProducts: view === 'products' });
    } else {
      setSearchRoute(makeSearchRoute({
        ...confirmedFilters,
        text,
        similarityThreshold: enableVisualSearchSetting ? { value: searchProps?.similarityThreshold || Number(CLIPEmbeddingsSimilarity).toFixed(2) } : '',
      }), { isProducts: view === 'products' });
    }
    Logger.log('User', 'Search', {
      text: searchValue,
      ...location.query,
    });

    const booleanSearchOperatorsInUse = new Set();
    booleanSearchOperators.forEach((operator) => {
      if (searchValue.includes(operator)) {
        booleanSearchOperatorsInUse.add(operator);
      }
    });
    if (booleanSearchOperatorsInUse.size) {
      Logger.log('User', 'SearchWithBooleanOperators', {
        operators: Array.from(booleanSearchOperatorsInUse),
      });
    }
  };

  const resetSearchValue = useCallback(() => {
    Logger.log('User', 'FindSimilarImagesResetSearchClicked');
    setSearchValue('');

    setSearchRoute(makeSearchRoute({
      ...confirmedFilters, text: '', byImageId: '', similarityThreshold: '',
    }), { isProducts: view === 'products' });
  }, [confirmedFilters, makeSearchRoute, view]);

  useEffect(() => {
    const widths = itemsRef?.current?.reduce((acc, ref) => {
      if (ref?.current) {
        const width = ref.current.clientWidth;
        acc[ref.current.dataset.id] = { width };
      }
      return acc;
    }, itemWidths || {});
    setItemWidths(widths);
  }, [itemWidths, renderItems]);

  const recalculateItems = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    // here 20px for the 10px padding for left and right sides
    const containerWidth = container.clientWidth - (controlsRef?.current?.clientWidth || 144) - 25;
    let totalWidth = 0;
    let visibleItemsCount = 0;
    // Measure the width of the plus indicator (if it is rendered)
    const plusWidth = hiddenItemsCounterRef.current ? hiddenItemsCounterRef.current.clientWidth : 70;
    for (let i = 0; i < renderItems.length; i++) {
      const { id } = renderItems[i];
      const { width: itemWidth } = itemWidths[id] || { width: 0 };
      // If there are more items after this one, account for the plus indicator's width
      const additionalSpace = (i < renderItems.length - 1) ? plusWidth : 0;
      if (totalWidth + itemWidth + additionalSpace < containerWidth) {
        totalWidth += itemWidth;
        visibleItemsCount++;
      } else { break; }
    }
    if (visibleItemsCount !== renderItems.length || visibleItemsCount !== items.length) {
      const newItems = [...renderItems];
      newItems.length = visibleItemsCount;
      setItems(newItems);
      setHiddenItemsCount(renderItems.length - visibleItemsCount);
    }
  }, [itemWidths, items.length, renderItems]);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    setIsHovering(false);
    recalculateItems();
    utils.LocalStorage.set('picsio.searchFiltersMinimized', true);
    Logger.log('User', 'MinimizeFiltersPanel', { screenSize: { width: window.screen.width, height: window.screen.height } });
  }, [recalculateItems]);

  const expand = useCallback((e, initiatedFrom) => {
    setIsCollapsed(false);
    setIsHovering(false);
    utils.LocalStorage.set('picsio.searchFiltersMinimized', false);
    Logger.log('User', 'ExpandFiltersPanel', { initiatedFrom, screenSize: { width: window.screen.width, height: window.screen.height } });
  }, []);

  useEffect(() => {
    recalculateItems();
    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(() => recalculateItems());
    if (container) {
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', recalculateItems);

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      window.removeEventListener('resize', recalculateItems);
    };
  }, [renderItems]);

  useEffect(() => {
    // sometimes styles for the filters are not applied, which causes wrong calculations
    const timer = setTimeout(() => {
      recalculateItems();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeout.current);
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeout.current = setTimeout(() => setIsHovering(false), 500);
  }, []);

  const hiddenItemsHaveActiveFilter = useMemo(() => {
    const activeFiltersIds = Object.keys(confirmedFilters).filter((id) => id !== 'text' && id !== 'byImageId' && id !== 'similarityThreshold' && id !== 'searchSource' && id !== 'collectionIdsOperator');

    return activeFiltersIds.every((id) => {
      if (id === 'collectionIds' && confirmedFilters[id]?.value?.length === 1 && confirmedFilters[id].value[0]._id === rootCollection?._id) {
        return true;
      } if (!items.find((item) => item.id === id)) {
        return false;
      } return true;
    });
  }, [confirmedFilters, items, rootCollection?._id]);

  const toggleInputFocus = useCallback(() => {
    setIsFocused(!isFocused);
  }, [isFocused]);

  const handleGoBack = useCallback(() => {
    Logger.log('User', 'FindSimilarImagesBackBtnClicked');
    navigate(-1);
  }, [navigate]);

  const handleChangeImageSimilarityLevel = useCallback((level) => {
    setSearchRoute(
      makeSearchRoute({
        ...confirmedFilters,
        byImageId: { value: searchProps?.byImageId },
        similarityThreshold: { value: (level === 100 ? 99 : level) / 100 },
      }),
      { isProducts: view === 'products' },
    );
  }, [confirmedFilters, makeSearchRoute, searchProps, view]);

  const handleChangeTextSimilarityLevel = useCallback((realValue) => {
    const knnScore = 0.1 + ((realValue - 10) / 40) * 0.4;
    setSearchRoute(
      makeSearchRoute({
        ...confirmedFilters,
        text: searchValue,
        similarityThreshold: { value: Number(knnScore.toFixed(2)) },
      }),
      { isProducts: view === 'products' },
    );
  }, [confirmedFilters, makeSearchRoute, searchValue, view]);

  const searchInputNode = (
    <div id="searchInput">
      <div className="searchHeader">
        <Choose>
          <When condition={!!searchProps?.byImageId && !picsioConfig.isPim && picsioConfig.isMainApp && searchByAsset}>
            <div className="searchByImageInfo">
              <IconButton
                size="md"
                className="searchByImageInfo__btnBack btnBack"
                componentProps={{
                  'data-testid': 'findSimilarImagesBackBtn',
                }}
                onClick={handleGoBack}
              >
                <AutoSuggestDown />
              </IconButton>
              <div className="searchByImageInfo__imageDetails">
                <If condition={searchByAsset?.thumbnail?.small}>
                  <Tooltip
                    content={(
                      <div className="zoomContainer">
                        <img className="zoomImage" src={searchByAsset?.thumbnail?.small} alt="find-similar" />
                      </div>
                    )}
                    placement="bottom"
                  >
                    <div className="searchByImageInfo__thumbnail">
                      <img src={searchByAsset?.thumbnail?.small} alt="find-similar" />
                    </div>
                  </Tooltip>
                </If>
                <If condition={searchByAsset?.name}>
                  <Tooltip content={searchByAsset?.name} placement="bottom">
                    <div className="searchByImageInfo__imageName">{searchByAsset?.name}</div>
                  </Tooltip>
                </If>
              </div>
              <IconButton
                size="xl"
                className="searchByImageInfo__btn btnReset"
                componentProps={{
                  'data-testid': 'btnResetSearchByImage',
                }}
                onKeyPress={resetSearchValue}
                onClick={resetSearchValue}
              >
                <Close />
              </IconButton>
              <Counter view={view} amount={view === 'products' ? products.search.total : assets.total} className="searchByImageInfo__counter" />
            </div>
            <div className={cn('imageSimilarityLevel')}>
              <RangeSliderByImage
                initValue={Number(searchProps?.similarityThreshold) * 100 || 55}
                onAfterChange={handleChangeImageSimilarityLevel}
              />
            </div>
          </When>
          <When condition={!!searchProps?.byImageId && !picsioConfig.isPim && picsioConfig.isMainApp && !assets.isLoaded}>
            <div className="searchByImageInfo">
              <div className="searchByImageInfo__loaderContainer">
                <span className="searchByImageInfo__loader" />
              </div>
              <span className="searchByImageInfo__imageName">
                {l10n.SEARCH.text.placeholderLoadSearch}
              </span>
            </div>
          </When>
          <When condition={!!searchProps?.byImageId && !picsioConfig.isPim && picsioConfig.isMainApp && (assets.isLoaded && !searchByAsset)}>
            <div className="searchByImageInfo">
              <IconButton
                size="md"
                className="searchByImageInfo__btnBack btnBack"
                componentProps={{
                  'data-testid': 'findSimilarImagesBackBtn',
                }}
                onClick={handleGoBack}
              >
                <AutoSuggestDown />
              </IconButton>
              <IconButton
                size="xl"
                className="searchByImageInfo__btn btnReset"
                componentProps={{
                  'data-testid': 'btnResetSearchByImage',
                }}
                onKeyPress={resetSearchValue}
                onClick={resetSearchValue}
              >
                <Close />
              </IconButton>
              <Counter view={view} amount={view === 'products' ? products.search.total : assets.total} className="searchByImageInfo__counter" />
            </div>
          </When>
          <Otherwise>
            <span className="btnSearch">
              <Icon size="md">
                <SearchIcon />
              </Icon>
            </span>
            <Autocomplete
              placeholder={getSearchPlaceholder}
              searchValue={searchValue}
              handleChange={handleSearchValueChange}
              handleSubmit={submitSearchValue}
              handleFocus={toggleInputFocus}
              handleBlur={toggleInputFocus}
              fromSearchFilters
              inputRef={inputRef}
            />
            <If condition={!isFocused}>
              <Counter view={view} amount={view === 'products' ? products.search.total : assets.total} className="filters" />
            </If>
            <If condition={searchValue.length > 0}>
              <IconButton
                size="xl"
                className="btnReset"
                componentProps={{
                  'data-testid': 'btnResetSearchValue',
                }}
                onKeyPress={resetSearchValue}
                onClick={resetSearchValue}
              >
                <Close />
              </IconButton>
            </If>
            <If condition={enableVisualSearchSetting && searchValue.length > 0 && !isFocused}>
              <div className={cn('imageSimilarityLevel')}>
                <RangeSliderByText
                  initValue={searchProps?.similarityThreshold ? Number(searchProps.similarityThreshold) * 100 : Number(CLIPEmbeddingsSimilarity).toFixed(2) * 100}
                  onAfterChange={handleChangeTextSimilarityLevel}
                />
              </div>
            </If>
            <If condition={searchValue.length > 0 && isFocused}>
              <div
                role="presentation"
                className="picsioDefBtn btnSearch btnCallToAction btnSubmitText"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  Logger.log('User', 'AdvancedSearchPanelConfirm', { ...location.query });
                  submit({ ...confirmedFilters, text: searchValue });
                  inputRef.current.blur();
                }}
              >
                {l10n.SEARCH.text.Search}
              </div>
            </If>
          </Otherwise>
        </Choose>
      </div>
    </div>
  );

  return (
    <>
      <div className={cn('searchFilters__wrapper', { onlyTextSearch })} style={{ justifyContent: picsioConfig.isProofing ? 'unset' : 'space-between' }}>
        <div className="wrapperSearch searchFilters">
          <Choose>
            <When condition={searchInputTarget}>
              {ReactDOM.createPortal(searchInputNode, searchInputTarget)}
            </When>
            <Otherwise>
              {searchInputNode}
            </Otherwise>
          </Choose>
        </div>
        <If condition={!onlyTextSearch}>
          <div
            ref={containerRef}
            className="searchFilters__container"
            style={{
              display: customizingMode ? 'flex' : 'inline-block',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <If condition={isHovering && hiddenItemsCount > 0}>
              <Tooltip placement="top" content={isCollapsed ? l10n.RESIZER.expandFilters : l10n.RESIZER.collapseFilters}>
                <div className="expand">
                  <Choose>
                    <When condition={isCollapsed}>
                      <IconButton onClick={(e) => expand(e, 'arrowHoverClick')}>
                        <ArrowBottomSmall />
                      </IconButton>
                    </When>
                    <Otherwise>
                      <IconButton onClick={collapse}>
                        <ArrowTopSmall />
                      </IconButton>
                    </Otherwise>
                  </Choose>
                </div>
              </Tooltip>
            </If>
            <If condition={!customizingMode}>
              <div ref={controlsRef} className="searchFilters__controls">
                <If condition={picsioConfig.isMainApp}>
                  <Choose>
                    <When condition={isCollapsed && !resetIsHidden}>
                      <Tooltip
                        content={l10n.SEARCH.resetFiltersBtn}
                        placement="top"
                      >
                        <IconButton
                          size="md"
                          className="searchFilters__iconBtn addMore"
                          color="primary"
                          onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                        >
                          <Close />
                        </IconButton>
                      </Tooltip>
                    </When>
                    <Otherwise>
                      <Tooltip
                        content={l10n.SEARCH.addMoreFilters}
                        placement="top"
                      >
                        <IconButton
                          size="md"
                          className="searchFilters__iconBtn addMore"
                          onClick={handleCustomizingMode}
                        >
                          <AddMoreFilters />
                        </IconButton>
                      </Tooltip>
                    </Otherwise>
                  </Choose>
                  <Tooltip
                    content={l10n.SEARCH.saveSearch}
                    placement="top"
                  >
                    <IconButton
                      size="md"
                      className="searchFilters__iconBtn"
                      disabled={!Object.keys(selectedFilters).length}
                      onClick={handleSavedSearchSave}
                    >
                      <SearchPlusIcon />
                    </IconButton>
                  </Tooltip>
                </If>
                <If condition={isRouteSearch(location.path)}>
                  <Tooltip
                    content={l10n.SEARCH.sort}
                    placement="top"
                  >
                    <div>
                      <ToolbarSort
                        sortType={sortType}
                        changeSort={handleChangeSort}
                        openOnClick
                      />
                    </div>
                  </Tooltip>
                </If>
                <If condition={!isRouteProducts(location.pathname)}>
                  <Tooltip
                    content={l10n.SEARCH.view}
                    placement="top"
                  >
                    <div>
                      <CatalogViewMode openOnClick leftFilter />
                    </div>
                  </Tooltip>
                </If>
              </div>
            </If>
            <div
              className="firstSearchItem"
              data-id="first"
              onDragEnter={dragenter}
            />
            {
              (isCollapsed && hiddenItemsCount && !customizingMode ? items : renderItems).map((item, i) => (
                <SearchFilterItem
                  key={item.id}
                  ref={itemsRef.current[i]}
                  id={item.id}
                  data={item}
                  filterData={selectedFilters[item.id]}
                  confirmedData={confirmedFilters[item.id]}
                  customizingMode={customizingMode}
                  isSelected={checkActive(item.id)}
                  isOpen={checkOpen(item.id)}
                  removable={!isPinnedSearchFilter(item.id, pinnedFilterIds)}
                  filters={FILTERS}
                  rootCollectionId={rootCollection?._id}
                  onSelectFilter={handleSelectFilter}
                  onConfirmFilter={handleConfirmFilter}
                  onResetFilter={handleResetFilter}
                  onOpen={handleOpen}
                  onClose={handleClose}
                  onRemove={handleRemoveFilter}
                  onDragStart={dragstart}
                  onDragEnter={dragenter}
                  onDragEnd={dragend}
                />
              ))
            }
            <If condition={isCollapsed && hiddenItemsCount && !customizingMode}>
              <Tooltip content={l10n.RESIZER.expandFilters} placement="top">
                <div
                  ref={hiddenItemsCounterRef}
                  data-id="hiddenItems"
                  style={{
                    position: 'relative', display: 'inline-block',
                  }}
                  onClick={(e) => expand(e, 'aggregatedBadgeClick')}
                >
                  <div className={cn('searchFilterItem', {
                    active: !hiddenItemsHaveActiveFilter,
                  })}
                  >
                    <div className="searchFilterItem__title">{`+${hiddenItemsCount}`}</div>
                  </div>
                </div>
              </Tooltip>
            </If>
            <If condition={picsioConfig.isMainApp}>
              <If condition={customizingMode}>
                <AddFiltersBtn onAddFilters={handleShowAddFiltersDialog} />
              </If>
            </If>
            <If condition={!customizingMode && Object.keys(confirmedFilters).length && !resetIsHidden && !isCollapsed}>
              <div
                data-id="resetFilters"
                style={{
                  position: 'relative', display: 'inline-block',
                }}
              >
                <span
                  className="picsioLink"
                  onClick={(e) => handleResetAllFilters(e, 'expandedView')}
                >
                  {l10n.SEARCH.resetFiltersBtn}
                </span>
              </div>
            </If>
          </div>
        </If>

        <If condition={customizingMode}>
          <div className="searchFilters__controls" style={{ padding: '10px 12px' }}>
            <IconButton
              size="md"
              className="searchFilters__iconBtn searchFilters__customizeIconBtn"
              onClick={handleCancelCustomizingMode}
            >
              <CloseIcon />
            </IconButton>
            <Tooltip
              content={l10n.SEARCH.saveFiltersTooltip}
              placement="top"
            >
              <IconButton
                size="md"
                className="searchFilters__iconBtn searchFilters__customizeIconBtn"
                onClick={handleSaveFilters}
              >
                <CheckIcon />
              </IconButton>
            </Tooltip>
          </div>
        </If>
      </div>
    </>
  );
};

export default SearchFilters;
